// ============================================================================
// UK Statutory Variables — single source of truth.
//
// Every calculator, blog post, calculator FAQ and policy template should read
// the relevant figure from here, not hard-code it. Updating a rate at the
// start of the tax year is then a one-line change.
//
// Figures are indicative 2026/27 values; verify against gov.uk before
// publishing rate-sensitive client advice.
// ============================================================================

export const TAX_YEAR = "2026/27" as const;
/** ISO date the table below was last verified against gov.uk. */
export const STATUTORY_LAST_REVIEWED = "2026-04-06";

// ────────────────────────────────────────────────────────────────────────────
// Redundancy
// ────────────────────────────────────────────────────────────────────────────
export const REDUNDANCY = {
  /** Statutory weekly pay cap (England, Scotland & Wales). */
  weeklyPayCap: 700,
  /** Maximum years of continuous service counted. */
  serviceYearsCap: 20,
  /** Weeks of pay per year of service, by age during that year. */
  ratePerYear: { under22: 0.5, age22to40: 1, age41plus: 1.5 } as const,
  /** Minimum continuous service for entitlement. */
  qualifyingYears: 2,
} as const;

// ────────────────────────────────────────────────────────────────────────────
// Statutory Sick Pay (SSP)
// ────────────────────────────────────────────────────────────────────────────
export const SSP = {
  /** Weekly SSP rate. */
  weeklyRate: 116.75,
  /** Maximum weeks SSP payable in any one period of incapacity. */
  maxWeeks: 28,
  /** Unpaid waiting days at the start of a period of incapacity. */
  waitingDays: 3,
  /** Lower Earnings Limit — minimum average weekly earnings to qualify. */
  lowerEarningsLimit: 125,
} as const;

// ────────────────────────────────────────────────────────────────────────────
// Holiday entitlement (Working Time Regulations 1998)
// ────────────────────────────────────────────────────────────────────────────
export const HOLIDAY = {
  /** Statutory minimum weeks of paid leave per year. */
  weeksPerYear: 5.6,
  /** Statutory cap on days for a full-timer. */
  daysCap: 28,
  /** Accrual rate for irregular-hours / part-year workers. */
  irregularAccrualRate: 0.1207, // 12.07%
} as const;

// ────────────────────────────────────────────────────────────────────────────
// Statutory Maternity / Paternity / Adoption / Shared Parental / Bereavement
// ────────────────────────────────────────────────────────────────────────────
export const FAMILY_PAY = {
  /** Standard weekly rate for SMP (after the first 6 weeks at 90%), SPP, SAP, ShPP, SPBP. */
  standardWeeklyRate: 187.18,
  /** SMP: first 6 weeks paid at 90% of average weekly earnings. */
  smpHigherRateWeeks: 6,
  smpHigherRatePercent: 0.9,
  /** Total weeks of SMP entitlement. */
  smpTotalWeeks: 39,
} as const;

// ────────────────────────────────────────────────────────────────────────────
// National Minimum / Living Wage (from April 2026)
// ────────────────────────────────────────────────────────────────────────────
export const NMW = {
  nationalLivingWage21Plus: 12.21,
  age18to20: 10.0,
  under18: 7.55,
  apprentice: 7.55,
} as const;

// ────────────────────────────────────────────────────────────────────────────
// Employment Tribunal
// ────────────────────────────────────────────────────────────────────────────
export const TRIBUNAL = {
  /** Compensatory award cap for ordinary unfair dismissal — lower of this or 52 weeks' pay. */
  unfairDismissalCompensatoryCap: 118_223,
  /** Maximum ACAS Code uplift. */
  acasCodeUpliftMax: 0.25,
  /** Minimum continuous service for ordinary unfair dismissal claim. */
  unfairDismissalQualifyingYears: 2,
} as const;

// ────────────────────────────────────────────────────────────────────────────
// Statutory minimum notice (Employment Rights Act 1996 s.86)
// ────────────────────────────────────────────────────────────────────────────
export const NOTICE = {
  /** Minimum service (months) before any statutory notice is owed. */
  qualifyingMonths: 1,
  /** Weeks owed by employer after 1 month and before 2 years' service. */
  employerMinWeeks: 1,
  /** After 2 years: 1 week per complete year of service, capped. */
  employerWeeksPerYear: 1,
  employerWeeksCap: 12,
  /** Statutory minimum an employee must give after 1 month's service. */
  employeeMinWeeks: 1,
} as const;

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

/** Formatted GBP value. */
export function gbp(amount: number, opts: { decimals?: number } = {}): string {
  return `£${amount.toLocaleString("en-GB", {
    minimumFractionDigits: opts.decimals ?? 0,
    maximumFractionDigits: opts.decimals ?? 2,
  })}`;
}

/** Rate per year of redundancy service for an employee at `ageDuringYear`. */
export function redundancyRateForAge(ageDuringYear: number): number {
  if (ageDuringYear >= 41) return REDUNDANCY.ratePerYear.age41plus;
  if (ageDuringYear >= 22) return REDUNDANCY.ratePerYear.age22to40;
  return REDUNDANCY.ratePerYear.under22;
}
